import React from 'react';
import moment from 'moment';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  RadioGroup,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Switch,
  TableContainer,
  TextField,
  Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import CircleIcon from '@mui/icons-material/Circle';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PostAddIcon from '@mui/icons-material/PostAdd';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';

import PropTypes from 'prop-types';

import API from '../../data/api';
import CustomTablePagination from '../custom/CustomTablePagination';
import CustomPdfViewer from '../custom/CustomPdfViewer';
import { isNotNullValue } from '../../utils/formUtil';
import { getUserInfo, isAuthorizedRole } from '../../utils/authUtil';
import { OrderCards } from './modals/OrderCards';
import FileDragzone from '../ui/FileDragzone';
import palette from '../../theme/palette';
import CustomModal from '../custom/CustomModal';
import { FormItemlGrid } from '../ui/FormItemlGrid';
import { filePreviewStyle, MAX_FILE_SIZE_MB } from '../../utils/constants';
import { fNumber } from '../../utils/formatNumber';
import RadioButton from '../ui/RadioButton';

const approvalTitle = 'Approve Order';
const viewDocumentTitle = 'View PFI/POP Document';
const uploadTitle = 'Upload a PFI/POP Document';

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const initState = {
  openModal: false,
  orderID: '',
  reason: '',
  reasonError: false,
  orderPo: '',
  orderPoError: false,
  pricePerLiter: '',
  pricePerLiterError: false,
  productId: '',
  productName: '',
  orderItemID: '',
  selectedDepot: '',
  selectedDepotError: false,
  uploadedFiles: [],
  filePreviewUrl: null,
  isFileAttached: false,
  yOverflow: 'auto',
  modalStyle: {},
  isDisabledPrice: !isAuthorizedRole(["FINANCE-C2", "FINANCE-L1"]),
  selectedOrder: null,
  verdict: null,
  verdictError: false,
  canEditOrder: false,
  isPoPUploaded: false,
  skipPfiUpload: false,
}
export default class AllOrders extends React.Component {
  constructor(props) {
    super(props);
    const userInfo = getUserInfo() || {};
    const roleList = userInfo?.realm_access?.roles || [];
    const isDepotRole = roleList.some((role) => String(role || '').startsWith('DEPOT-'));
    this.state = { 
      allOrders: [],
      depots: [],
      page: 1,
      records: 15,
      totalRecords: 1,
      totalPages: 1,
      recordsList: [5, 10, 15, 20],
      loadingOrders: false,
      orderSource: 'all',
      sourceCounts: {
        all: 0,
        approved: 0,
      },
      isClient: false,
      proofOfPayment: null,
      isPoPUploaded: false,
      modalTitle: approvalTitle,
      isDepotRole,
      actionMenuAnchorEl: null,
      actionMenuOrder: null,
      showDepotSummaryModal: false,
      depotSummaryLoading: false,
      depotSummaryError: '',
      depotSummaryOrder: null,
      depotSummarySuborders: [],
      depotSummaryTotalSuborders: 0,
      depotSummaryDocuments: [],
      showCreateSuborderModal: false,
      createSuborderLoading: false,
      createSuborderSubmitting: false,
      createSuborderError: '',
      createSuborderOrder: null,
      showSuborderTrackModal: false,
      selectedTrackedSuborder: null,
      createSuborderAddresses: [],
      createSuborderForm: {
        orderItemId: '',
        companyId: '',
        companyName: '',
        totalVolume: 0,
        remainingVolume: 0,
        volume: '',
        destination: '',
        deliveryDate: '',
      },
      ...initState 
    }
  }

  componentDidMount() {
    this.getAllOrders(this.props.filterString);
    this.refreshOrderSourceCounts(this.props.filterString);
    this.fetchDepots();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps?.filterString !== this.props.filterString || this.props.updateAllOders
    ) {
      this.getAllOrders(this.props.filterString);
      this.refreshOrderSourceCounts(this.props.filterString);
      this.props.setUpdateAllOders(false);
    }
  }

  forceRerender = () => {
    this.forceUpdate();
  }

  getOdersByCategory = () => {
    let category = "";
    if (this.props.orderCategory === 'buy') { // buy
      category = "&is_supplier=true";
    }
    else if (this.props.orderCategory === 'sell') { // sell
      category = "&is_client=true";
    }
    return category;
  }

  getOrdersEndpoint = (source = this.state.orderSource, records = this.state.records, page = this.state.page, finalQuery = '') => {
    let endpoint = '';
    if (source === 'approved') {
      endpoint = `inventory/backoffice/approved_order_items?records=${records}&page=${page}`;
    } else {
      const category = this.getOdersByCategory();
      endpoint = `inventory/backoffice/order_items?records=${records}&page=${page}${category}`;
    }

    if (finalQuery) {
      endpoint += finalQuery;
    }

    return endpoint;
  };

  refreshOrderSourceCounts = async (finalQuery = '') => {
    if (!this.hasDepotOrderActionsEnabled()) {
      return;
    }

    try {
      const [allResponse, approvedResponse] = await Promise.all([
        API.get(this.getOrdersEndpoint('all', 1, 1, finalQuery)),
        API.get(this.getOrdersEndpoint('approved', 1, 1, finalQuery)),
      ]);

      this.setState({
        sourceCounts: {
          all: toNumber(allResponse?.data?.total_orders),
          approved: toNumber(approvedResponse?.data?.total_orders),
        },
      });
    } catch (error) {
      console.error('Error fetching order source counts:', error);
      this.setState({
        sourceCounts: {
          all: 0,
          approved: 0,
        },
      });
    }
  };

  handleOrderSourceChange = (source) => {
    if (!source || source === this.state.orderSource) {
      return;
    }

    this.setState(
      {
        orderSource: source,
        page: 1,
        actionMenuAnchorEl: null,
        actionMenuOrder: null,
      },
      () => {
        this.getAllOrders(this.props.filterString);
      },
    );
  };

  getAllOrders = async (finalQuery = '') => {
    const { records, page } = this.state;
    const endpoint = this.getOrdersEndpoint(this.state.orderSource, records, page, finalQuery);
  
    try {
      this.setState({ loadingOrders: true });
      // Perform the GET request
      try {
        const res = await API.get(endpoint);
        // console.log(`ORDERS -> status [${res.status}]`);

        if (res.status === 200) {
          // console.log(`ORDER ITEMS: ${res.status}`);
          const entries = res.data.orders;
          this.setState({
            allOrders: entries,
            totalPages: Math.min(res.data.total_pages, Math.ceil(entries?.length/this.state.records)),
            totalRecords: res.data.total_orders,
            loadingOrders: false,
          });
          this.props.setUpdateAllOders(false);
        } else {
          this.setState({ loadingOrders: false });
          console.log("Error fetching all orders:", res.status);
        }
      } catch (error) {
        this.setState({ loadingOrders: false });
        console.log("Error fetching all orders:", error);
      }

    } catch (error) {
      this.setState({ loadingOrders: false });
      console.error('Preflight request error:', error);
    }
  };
  

  fetchDepots = async () => {
    try {
      const response = await API.get('inventory/backoffice/depots?depot_type=pricing');
      this.setState({ depots: response.data.depots });
    } catch (error) {
      console.error('Error fetching depots:', error);
    }
  };

  submitOrder = async () => {
    const { orderID, orderPo, reason, pricePerLiter, selectedDepot, orderItemID, productId, verdict, isClient } = this.state;

    let endpoint; 
    let conTemp;
  
    // Ensure that the fileds are provided
    if(this.isInvalidateApprovalForm()) {
      return
    }

    // 
    if (verdict === 'approve') {
        endpoint = 'inventory/backoffice/approve_order/';
        conTemp = `${endpoint}${orderID}?purchase_order=${orderPo}`
          .concat(`&order_id=${orderID}`)
          .concat(`&is_client=${isClient}`)
          .concat(`&reason=${reason || 'null'}`)
          .concat(`&order_item_id=${orderItemID}`)
          .concat(`&pfi_uploaded=${this.state.skipPfiUpload}`)

        if (pricePerLiter) {
            conTemp += `&price_per_liter=${pricePerLiter}`;
        }
        if (selectedDepot) {
            conTemp += `&depot_id=${selectedDepot}`;
        }
        if (productId) {
            conTemp += `&product_id=${productId}`;
        }
    } else if (verdict === 'decline') {
      if(this.isEmptyField('reason')) {
        return
      }
        endpoint = 'inventory/backoffice/decline_order/';
        conTemp = `${endpoint}${orderItemID}?purchase_order=${orderPo}&order_id=${orderID}&is_client=${isClient}&reason=${reason || 'null'}`;
    } else {
      this.setState({
        verdictError: true,
      })
      return
    }

    try {
      const res = await API.patch(conTemp);
      if (res.status === 206) {
        // this.setState({ openModal: false, selectedDepot: '', orderPo: '', reason: '', pricePerLiter: '' });
        this.handleCloseModal();
        this.props.setUpdateStats(true);
        this.props.setUpdateAllOders(true);
      } else {
        console.log("Error:", res.data);
      }
    } catch (error) {
      console.log("Error submitting order:", error);
    }
  };

  handleSubmitDocument = async () => {
    const { proofOfPayment, orderItemID, isFileAttached } = this.state;

    if (!proofOfPayment) return;
    if (proofOfPayment && !isFileAttached) {
      this.setState({
        modalStyle: filePreviewStyle,
        isFileAttached: true,
        modalTitle: viewDocumentTitle,
      });
      return;
    }

    const formData = new FormData();
    formData.append('order_confirmation_file', proofOfPayment);
    try {
      const res = await API.post(`inventory/backoffice/store_order_confirmation/${orderItemID}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (res.status === 200) {
        this.handleChangeToApprovalView();
      } else {
        console.log("Error:", res.data);
      }

    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  handleChangeToApprovalView = (skipPfiUpload=false) => {
    this.setState({
      proofOfPayment: null,
      uploadedFiles: [],
      modalStyle: {},
      isFileAttached: false,
      isPoPUploaded: true,
      modalTitle: approvalTitle,
      skipPfiUpload: skipPfiUpload
    });
  }

  handleProofOfPaymentChange = (files) => {
    const file = (files.length > 0)? files[0] : null;
    this.setState({ 
        proofOfPayment: file,
        uploadedFiles: files,
        filePreviewUrl: file? URL.createObjectURL(file) : null,
    });
  };

  nextPage = (action) => {
    const { totalPages } = this.state;
    this.setState((prevState) => {
      let currentPage = prevState.page;
      if (action === 'next') {
        currentPage = Math.min(currentPage + 1, totalPages);
      } else {
        currentPage = Math.max(currentPage - 1, 1);
      }
      return { page: currentPage };
    }, () => {
      this.getAllOrders(this.props.filterString);
    });
  };

  handleDepotSelect = (event) => {
    const selectedValue = event.target.value;
    this.setState({ selectedDepot: selectedValue })

    const currentDepot = this.state.depots?.find(d => d.depot_id === selectedValue)?.prices;
    if (isNotNullValue(currentDepot)){
      const product = currentDepot?.find(d => d.product_id === this.state.productId);
      const price = product?.price_details?.sell_price || '';
      this.setState({ 
        pricePerLiter: price,
        pricePerLiterError: !isNotNullValue(price),
        selectedDepotError: false,
      })
      // console.log('selected depot: ', selectedValue, currentDepot, product)

    }

  }

  handleChangeRowsPerPage = (event) => {
    this.setState(() => {
      return { records: parseInt(event.target.value, 10), page: 1 };
    }, () => {
      this.getAllOrders(this.props.filterString);
    });
  }

  hasDepotOrderActionsEnabled = () => {
    return Boolean(this.props.enableDepotOrderActions && this.state.isDepotRole);
  };

  handleOpenActionMenu = (event, row) => {
    event.stopPropagation();
    this.setState({
      actionMenuAnchorEl: event.currentTarget,
      actionMenuOrder: row,
    });
  };

  handleCloseActionMenu = () => {
    this.setState({
      actionMenuAnchorEl: null,
      actionMenuOrder: null,
    });
  };

  handleOpenOrderSummary = async () => {
    const order = this.state.actionMenuOrder;
    const orderItemId = this.getOrderItemId(order);
    const orderId = this.getOrderId(order);
    if (!orderItemId && !orderId) {
      this.handleCloseActionMenu();
      return;
    }

    this.setState({
      actionMenuAnchorEl: null,
      actionMenuOrder: null,
      showDepotSummaryModal: true,
      depotSummaryLoading: true,
      depotSummaryError: '',
      depotSummaryOrder: {
        ...order,
        order_item_id: orderItemId,
        order_id: orderId,
      },
      depotSummarySuborders: [],
      depotSummaryTotalSuborders: 0,
      depotSummaryDocuments: [],
    });

    try {
      const summary = await this.fetchOrderSummaryData({ orderId, orderItemId });
      this.setState({
        depotSummaryLoading: false,
        depotSummarySuborders: summary.suborders,
        depotSummaryTotalSuborders: summary.totalSuborders,
        depotSummaryDocuments: summary.documents,
      });
    } catch (error) {
      console.error('Error fetching order summary:', error);
      this.setState({
        depotSummaryLoading: false,
        depotSummaryError: 'Unable to load order summary right now.',
      });
    }
  };

  fetchOrderSummaryData = async ({ orderId = '', orderItemId = '' } = {}) => {
    const endpoints = [];
    if (orderId) {
      endpoints.push(`depots/suborders?order_id=${orderId}`);
    }
    if (orderItemId) {
      endpoints.push(`inventory/backoffice/suborders/${orderItemId}`);
      endpoints.push(`backoffice/suborders/${orderItemId}`);
      endpoints.push(`inventory/backoffice/suborders?order_item_id=${orderItemId}`);
    }

    for (let i = 0; i < endpoints.length; i += 1) {
      const endpoint = endpoints[i];
      try {
        const response = await API.get(endpoint);
        if (response?.status !== 200) {
          // eslint-disable-next-line no-continue
          continue;
        }

        const payload = response?.data || {};
        let suborders = [];
        if (Array.isArray(payload?.suborders)) {
          suborders = payload.suborders;
        } else if (Array.isArray(payload?.rows)) {
          suborders = payload.rows;
        } else if (Array.isArray(payload?.data)) {
          suborders = payload.data;
        }
        const rawDocuments = payload?.documents || payload?.attachments || payload?.files || [];
        const documents = Array.isArray(rawDocuments) ? rawDocuments : [];
        const totalSuborders =
          toNumber(payload?.total_suborders)
          || toNumber(payload?.total_rows)
          || toNumber(payload?.total_records)
          || suborders.length;

        return {
          suborders,
          documents,
          totalSuborders,
        };
      } catch (error) {
        console.error(`Error fetching order summary from ${endpoint}:`, error);
      }
    }

    throw new Error('Unable to fetch order summary');
  };

  handleCloseOrderSummaryModal = () => {
    this.setState({
      showDepotSummaryModal: false,
      depotSummaryLoading: false,
      depotSummaryError: '',
      depotSummaryOrder: null,
      depotSummarySuborders: [],
      depotSummaryTotalSuborders: 0,
      depotSummaryDocuments: [],
    });
  };

  handleOpenCreateSuborderModal = async () => {
    const order = this.state.actionMenuOrder;
    const orderItemId = this.getOrderItemId(order);
    if (!orderItemId) {
      this.handleCloseActionMenu();
      return;
    }

    const approvalMeta = this.getOrderApprovalMeta(order);
    if (!approvalMeta.isApproved) {
      this.handleCloseActionMenu();
      return;
    }

    this.setState({
      actionMenuAnchorEl: null,
      actionMenuOrder: null,
      showCreateSuborderModal: true,
      createSuborderLoading: true,
      createSuborderSubmitting: false,
      createSuborderError: '',
      createSuborderOrder: order,
      createSuborderAddresses: [],
      createSuborderForm: {
        orderItemId,
        companyId: order.company_id || '',
        companyName: order.company_name || '',
        totalVolume: toNumber(order.volume),
        remainingVolume: toNumber(order.volume),
        volume: '',
        destination: '',
        deliveryDate: '',
      },
    });

    try {
      const remainingResponse = await API.get(`inventory/backoffice/get_remaining_volume/${orderItemId}`);
      const remainingPayload = remainingResponse.status === 200 ? remainingResponse.data : null;
      const companyId = remainingPayload?.company_id || order.company_id;
      const normalizedRemaining = toNumber(remainingPayload?.remaining_volume);
      const normalizedTotal = toNumber(remainingPayload?.total_ordered_volume || order.volume);

      let addresses = [];
      if (companyId) {
        const addressResponse = await API.get(`inventory/backoffice/addresses/${companyId}`);
        if (addressResponse.status === 200 && Array.isArray(addressResponse.data)) {
          addresses = addressResponse.data;
        }
      }

      const firstAddressId = addresses?.[0]?.id || addresses?.[0]?.address_id || '';

      this.setState({
        createSuborderLoading: false,
        createSuborderAddresses: addresses,
        createSuborderForm: {
          orderItemId: remainingPayload?.order_item_id || orderItemId,
          companyId: companyId || '',
          companyName: remainingPayload?.company_name || order.company_name || '',
          totalVolume: normalizedTotal,
          remainingVolume: normalizedRemaining || normalizedTotal,
          volume: '',
          destination: firstAddressId,
          deliveryDate: '',
        },
      });
    } catch (error) {
      console.error('Error preparing create suborder modal:', error);
      this.setState({
        createSuborderLoading: false,
        createSuborderError: 'Unable to load remaining volume details for this order.',
      });
    }
  };

  handleCloseCreateSuborderModal = () => {
    this.setState({
      showCreateSuborderModal: false,
      createSuborderLoading: false,
      createSuborderSubmitting: false,
      createSuborderError: '',
      createSuborderOrder: null,
      createSuborderAddresses: [],
      createSuborderForm: {
        orderItemId: '',
        companyId: '',
        companyName: '',
        totalVolume: 0,
        remainingVolume: 0,
        volume: '',
        destination: '',
        deliveryDate: '',
      },
    });
  };

  handleOpenSuborderTrackModal = (suborder = null) => {
    if (!suborder) {
      return;
    }
    this.setState({
      showSuborderTrackModal: true,
      selectedTrackedSuborder: suborder,
    });
  };

  handleCloseSuborderTrackModal = () => {
    this.setState({
      showSuborderTrackModal: false,
      selectedTrackedSuborder: null,
    });
  };

  handleCreateSuborderInput = (field, value) => {
    this.setState((prevState) => ({
      createSuborderForm: {
        ...prevState.createSuborderForm,
        [field]: value,
      },
      createSuborderError: '',
    }));
  };

  handleSubmitCreateSuborder = async () => {
    const { createSuborderForm } = this.state;
    const volume = toNumber(createSuborderForm.volume);
    const remainingVolume = toNumber(createSuborderForm.remainingVolume);
    const destination = createSuborderForm.destination;
    const deliveryDate = createSuborderForm.deliveryDate;

    if (!createSuborderForm.orderItemId) {
      this.setState({ createSuborderError: 'Order item ID is required.' });
      return;
    }
    if (volume <= 0) {
      this.setState({ createSuborderError: 'Volume must be greater than zero.' });
      return;
    }
    if (remainingVolume > 0 && volume > remainingVolume) {
      this.setState({ createSuborderError: `Volume cannot exceed remaining volume (${remainingVolume}).` });
      return;
    }
    if (!destination) {
      this.setState({ createSuborderError: 'Please select a destination.' });
      return;
    }
    if (!deliveryDate) {
      this.setState({ createSuborderError: 'Please select a delivery date.' });
      return;
    }

    this.setState({ createSuborderSubmitting: true, createSuborderError: '' });

    try {
      const delivery = moment(deliveryDate).format('YYYY-MM-DD HH:mm');
      const endpoint = `inventory/public_facing/suborder/${createSuborderForm.orderItemId}/${volume}/${destination}/${delivery}`;
      const response = await API.post(endpoint, '');

      if (response.status === 200 || response.status === 201) {
        this.handleCloseCreateSuborderModal();
        this.getAllOrders(this.props.filterString);
        this.refreshOrderSourceCounts(this.props.filterString);
        this.props.setUpdateStats(true);
      } else {
        this.setState({
          createSuborderSubmitting: false,
          createSuborderError: 'Unable to create suborder right now.',
        });
      }
    } catch (error) {
      console.error('Error creating suborder:', error);
      this.setState({
        createSuborderSubmitting: false,
        createSuborderError: 'Unable to create suborder right now.',
      });
    }
  };

  getBooleanFlag = (value) => {
    if (value === true || value === false) {
      return value;
    }
    if (typeof value === 'number') {
      return value > 0;
    }
    const normalizedValue = String(value || '').trim().toLowerCase();
    return ['true', '1', 'yes', 'y'].includes(normalizedValue);
  };

  getTriStateBoolean = (value) => {
    if (value === true || value === false) {
      return value;
    }
    if (typeof value === 'number') {
      if (value === 1) return true;
      if (value === 0) return false;
      return null;
    }
    if (value === null || value === undefined) {
      return null;
    }
    const normalizedValue = String(value).trim().toLowerCase();
    if (['true', '1', 'yes', 'y'].includes(normalizedValue)) {
      return true;
    }
    if (['false', '0', 'no', 'n'].includes(normalizedValue)) {
      return false;
    }
    return null;
  };

  getOrderApprovalMeta = (order = null) => {
    const approvedFlag = this.getTriStateBoolean(order?.approved);
    const hasApprovedAt = Boolean(order?.approved_at);

    if (approvedFlag === true || hasApprovedAt) {
      return {
        label: 'Approved',
        color: 'success',
        isApproved: true,
      };
    }

    if (approvedFlag === false) {
      return {
        label: 'Not Approved',
        color: 'error',
        isApproved: false,
      };
    }

    return {
      label: 'Pending Approval',
      color: 'warning',
      isApproved: false,
    };
  };

  getOrderItemId = (order = null) => (
    order?.order_item_id || order?.orderItemId || order?.id || ''
  );

  getOrderId = (order = null) => (
    order?.order_id || order?.orderId || ''
  );

  formatOrderDateTime = (value) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    const numericValue = Number(value);
    if (Number.isFinite(numericValue) && String(value).trim().match(/^\d+$/)) {
      return moment(numericValue).format('YYYY/MM/DD HH:mm');
    }

    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('YYYY/MM/DD HH:mm') : '-';
  };

  getSuborderTrackingChip = (suborder = null) => {
    const deliveredAt = suborder?.delivered_at;
    const allocation = String(suborder?.transport_allocation || '').trim().toLowerCase();
    const status = String(suborder?.status || '').trim().toLowerCase();

    if (deliveredAt || status === 'delivered') {
      return { label: 'Delivered', color: 'success' };
    }
    if (allocation && allocation !== 'not allocated') {
      return { label: 'In Transit', color: 'info' };
    }
    return { label: 'Pending Allocation', color: 'warning' };
  };

  getSuborderTrackingSteps = (suborder = null) => {
    const allocation = String(suborder?.transport_allocation || '').trim();
    const allocationReady = Boolean(allocation) && allocation.toLowerCase() !== 'not allocated';
    const deliveredReady = Boolean(suborder?.delivered_at) || String(suborder?.status || '').toLowerCase() === 'delivered';

    return [
      {
        key: 'created',
        label: 'Suborder Created',
        completed: Boolean(suborder?.created_at),
        value: this.formatOrderDateTime(suborder?.created_at),
        helper: 'Suborder has been created and queued for fulfillment.',
      },
      {
        key: 'allocation',
        label: 'Transport Allocation',
        completed: allocationReady,
        value: allocation || 'Not Allocated',
        helper: allocationReady
          ? 'Transport has been assigned to this suborder.'
          : 'Waiting for transporter assignment.',
      },
      {
        key: 'delivery',
        label: 'Delivered',
        completed: deliveredReady,
        value: deliveredReady ? this.formatOrderDateTime(suborder?.delivered_at) : 'Pending',
        helper: deliveredReady
          ? 'Suborder was marked as delivered.'
          : 'Delivery confirmation not yet submitted.',
      },
    ];
  };

  openModalWithOrder = (order) => {
    // console.log(`Opening modal with order: `, order);
    const isClient = this.getBooleanFlag(order?.is_client);
    const hasInteractiveSupplierAccess = this.getBooleanFlag(order?.interactive_supplier_flag);
    const isPfiUploaded = this.getBooleanFlag(order?.pfi_uploaded ?? order?.uploaded_pfi);
    const canOpenInteractiveSupplierOrder = isAuthorizedRole(["EXECUTIVE-C1", "DEMO", "FINANCE-C2"]);

    // Supplier has access to system, thus can approve
    if (
      isNotNullValue(order)
      && !isClient
      && hasInteractiveSupplierAccess
      && !canOpenInteractiveSupplierOrder
    ) {
      return;
    }

    this.setState({
      openModal: true,
      modalTitle: (isClient || isPfiUploaded) ? approvalTitle : uploadTitle,
      orderID: order.order_id,
      productId: order.product_id,
      productName: order.fuel_type,
      orderItemID: this.getOrderItemId(order),
      isClient: isClient,
      isPoPUploaded: isPfiUploaded,
      selectedOrder: order,
    });
  };

  handleCloseModal = () => {
    this.setState({ ...this.state, ...initState });
  };

  isEmptyField = (field) => {
    const empty = (this.state[field] === '' || this.state[field] === null || this.state[field] === undefined);
    if (empty) {
      const errorField = field + 'Error';
      this.setState({
        [errorField]: true,
      })
    }
    return empty;
  }

  isInvalidateApprovalForm = () => {
    let hasErrors = false;
    hasErrors = hasErrors || this.isEmptyField('orderPo');
    hasErrors = hasErrors ||this.isEmptyField('verdict');
    hasErrors = hasErrors ||(this.state.verdict === 'decline' && this.isEmptyField('reason'));
    hasErrors = hasErrors ||(this.state.verdict === 'approve' && this.isEmptyField('selectedDepot') && this.state.isClient);
    hasErrors = hasErrors || (this.state.verdict === 'approve' && this.isEmptyField('pricePerLiter'));

    return hasErrors;
  }

  render() {
    const {
      allOrders,
      loadingOrders,
      openModal,
      depots,
      isClient,
      isPoPUploaded,
      isFileAttached,
      orderSource,
      sourceCounts,
      actionMenuAnchorEl,
      showDepotSummaryModal,
      depotSummaryLoading,
      depotSummaryError,
      depotSummaryOrder,
      depotSummarySuborders,
      depotSummaryTotalSuborders,
      depotSummaryDocuments,
      showCreateSuborderModal,
      createSuborderLoading,
      createSuborderSubmitting,
      createSuborderError,
      createSuborderOrder,
      showSuborderTrackModal,
      selectedTrackedSuborder,
      createSuborderAddresses,
      createSuborderForm,
    } = this.state;

    const showDepotOrderActions = this.hasDepotOrderActionsEnabled();
    const showDepotActionsForCurrentSource = showDepotOrderActions && orderSource === 'approved';
    const showActionsColumn = showDepotActionsForCurrentSource;
    const actionMenuOrderApproval = this.getOrderApprovalMeta(this.state.actionMenuOrder);
    const canAddSuborderFromMenu = actionMenuOrderApproval.isApproved;
    return (
      <Paper sx={{ mt: 2, borderRadius: '10px' }}>
        {showDepotOrderActions && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ px: 1.5, pt: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <Button
              size="small"
              variant={orderSource === 'all' ? 'contained' : 'outlined'}
              onClick={() => this.handleOrderSourceChange('all')}
              sx={{ textTransform: 'none', borderRadius: 999, justifyContent: 'space-between', minWidth: 190 }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <span>Created Orders</span>
                <Chip
                  size="small"
                  label={toNumber(sourceCounts?.all)}
                  sx={{ height: 20 }}
                />
              </Stack>
            </Button>
            <Button
              size="small"
              variant={orderSource === 'approved' ? 'contained' : 'outlined'}
              onClick={() => this.handleOrderSourceChange('approved')}
              sx={{ textTransform: 'none', borderRadius: 999, justifyContent: 'space-between', minWidth: 190 }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <span>Approved Orders</span>
                <Chip
                  size="small"
                  label={toNumber(sourceCounts?.approved)}
                  sx={{ height: 20 }}
                />
              </Stack>
            </Button>
          </Stack>
        )}
 
        <TableContainer >
          <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>Order Number</TableCell>
              <TableCell>Client/Supplier Name</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Volume</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell>Approval</TableCell>
              {showActionsColumn && (
                <TableCell align="right">Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {allOrders.map((row) => {
              const orderItemId = this.getOrderItemId(row);
              const approvalMeta = this.getOrderApprovalMeta(row);
              return (
                <TableRow
                  key={orderItemId || `${row.order_id}-${row.created_date || row.created_datetime || ''}`}
                  sx={{

                  }}
                  onClick={() => {
                    if (!showDepotOrderActions) {
                      this.openModalWithOrder(row);
                    }
                  }}
                  selected={this.getOrderItemId(this.state.selectedOrder) === orderItemId}
                >
                  <TableCell component="th" scope="row">
                    {orderItemId || '-'}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={1}>
                      {(!row.is_client && row?.logo?.color) && (
                        <Tooltip title="Supplier">
                          <CircleIcon fontSize="small" sx={{ color: row?.logo?.color }} />
                        </Tooltip>
                      )}
                      {row.company_name}
                    </Stack>
                  </TableCell>
                  <TableCell>{row.fuel_type}</TableCell>
                  <TableCell>{fNumber(row.volume)}</TableCell>
                  <TableCell>{this.formatOrderDateTime(row.created_date || row.created_datetime)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={approvalMeta.color}
                      label={approvalMeta.label}
                      variant="filled"
                    />
                  </TableCell>
                  {showActionsColumn && (
                    <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                      <IconButton onClick={(event) => this.handleOpenActionMenu(event, row)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {loadingOrders && (
              <TableRow>
                <TableCell colSpan={showActionsColumn ? 7 : 6} align="center">
                  Loading orders...
                </TableCell>
              </TableRow>
            )}
            {!loadingOrders && allOrders.length === 0 && (
              <TableRow>
                <TableCell colSpan={showActionsColumn ? 7 : 6} align="center">
                  No orders found for this view.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {showDepotActionsForCurrentSource && (
        <Menu
          anchorEl={actionMenuAnchorEl}
          open={Boolean(actionMenuAnchorEl)}
          onClose={this.handleCloseActionMenu}
        >
          <MenuItem onClick={this.handleOpenOrderSummary}>
            <Stack direction="row" spacing={1} alignItems="center">
              <SummarizeOutlinedIcon fontSize="small" />
              <span>View Summary & Documents</span>
            </Stack>
          </MenuItem>
          <MenuItem onClick={this.handleOpenCreateSuborderModal} disabled={!canAddSuborderFromMenu}>
            <Stack direction="row" spacing={1} alignItems="center">
              <PostAddIcon fontSize="small" />
              <span>{canAddSuborderFromMenu ? 'Add Suborder' : 'Add Suborder (Requires Approval)'}</span>
            </Stack>
          </MenuItem>
        </Menu>
      )}

      <CustomTablePagination
        rowsPerPage={this.state.records}
        recordsList={this.state.recordsList}
        handleChangeRowsPerPage={this.handleChangeRowsPerPage}
        handleNextPage={this.nextPage}
        currentPage={this.state.page}
        totalPages={this.state.totalPages}
      />

      <Dialog
        open={showDepotSummaryModal}
        onClose={this.handleCloseOrderSummaryModal}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          {depotSummaryOrder?.order_id
            ? `Order ${depotSummaryOrder.order_id} Summary`
            : 'Order Summary'}
        </DialogTitle>
        <DialogContent dividers>
          {depotSummaryLoading && <p>Loading order summary...</p>}

          {!depotSummaryLoading && depotSummaryError && (
            <p style={{ color: palette.error.main }}>{depotSummaryError}</p>
          )}

          {!depotSummaryLoading && !depotSummaryError && depotSummaryOrder && (
            <Stack spacing={2}>
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper sx={{ p: 1.2, border: '1px solid', borderColor: 'divider' }}>
                    <p style={{ margin: 0, fontSize: 12, color: palette.grey[600] }}>Order Item ID</p>
                    <p style={{ margin: '4px 0 0', fontWeight: 700 }}>{depotSummaryOrder.order_item_id || '-'}</p>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper sx={{ p: 1.2, border: '1px solid', borderColor: 'divider' }}>
                    <p style={{ margin: 0, fontSize: 12, color: palette.grey[600] }}>Company</p>
                    <p style={{ margin: '4px 0 0', fontWeight: 700 }}>{depotSummaryOrder.company_name || '-'}</p>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper sx={{ p: 1.2, border: '1px solid', borderColor: 'divider' }}>
                    <p style={{ margin: 0, fontSize: 12, color: palette.grey[600] }}>Fuel Type</p>
                    <p style={{ margin: '4px 0 0', fontWeight: 700 }}>{depotSummaryOrder.fuel_type || '-'}</p>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper sx={{ p: 1.2, border: '1px solid', borderColor: 'divider' }}>
                    <p style={{ margin: 0, fontSize: 12, color: palette.grey[600] }}>Ordered Volume</p>
                    <p style={{ margin: '4px 0 0', fontWeight: 700 }}>{fNumber(depotSummaryOrder.volume)}</p>
                  </Paper>
                </Grid>
              </Grid>

              <Paper sx={{ border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ fontWeight: 700 }}>
                        {`Suborders Created (${toNumber(depotSummaryTotalSuborders) || depotSummarySuborders.length})`}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Suborder ID</TableCell>
                      <TableCell>Destination</TableCell>
                      <TableCell align="right">Volume</TableCell>
                      <TableCell>Delivery Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Track</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {depotSummarySuborders.map((suborder, index) => (
                      <TableRow key={`${suborder.suborder_id || 'sub'}-${index}`}>
                        <TableCell>{suborder.suborder_id || '-'}</TableCell>
                        <TableCell>
                          {suborder.destination_address
                            || suborder.delivery_location
                            || suborder.address
                            || suborder.destination
                            || '-'}
                        </TableCell>
                        <TableCell align="right">{fNumber(suborder.volume)}</TableCell>
                        <TableCell>
                          {this.formatOrderDateTime(
                            suborder.delivery_scheduled_datetime
                            || suborder.delivery_date
                            || suborder.estimated_delivery_date
                            || suborder.delivered_at
                            || suborder.created_at
                          )}
                        </TableCell>
                        <TableCell>{suborder.status || suborder.transport_allocation || '-'}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => this.handleOpenSuborderTrackModal(suborder)}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {depotSummarySuborders.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No suborders found for this order.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>

              <Paper sx={{ border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell colSpan={4} sx={{ fontWeight: 700 }}>Attached Documents</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>File Name</TableCell>
                      <TableCell>Document Number</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Open</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {depotSummaryDocuments.map((documentRow, index) => (
                      <TableRow key={`${documentRow.document_id || 'doc'}-${index}`}>
                        <TableCell>{documentRow.file_name || '-'}</TableCell>
                        <TableCell>{documentRow.document_number || '-'}</TableCell>
                        <TableCell>{documentRow.document_type || documentRow.type || '-'}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            onClick={() => window.open(documentRow.file_url, '_blank', 'noopener,noreferrer')}
                            disabled={!documentRow.file_url}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {depotSummaryDocuments.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No documents attached for this order.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleCloseOrderSummaryModal}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showSuborderTrackModal}
        onClose={this.handleCloseSuborderTrackModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {selectedTrackedSuborder?.suborder_id
            ? `Track Suborder ${selectedTrackedSuborder.suborder_id}`
            : 'Track Suborder'}
        </DialogTitle>
        <DialogContent dividers>
          {selectedTrackedSuborder && (
            <Stack spacing={2}>
              <Grid container spacing={1.2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Paper sx={{ p: 1.2, border: '1px solid', borderColor: 'divider' }}>
                    <p style={{ margin: 0, fontSize: 12, color: palette.grey[600] }}>Product</p>
                    <p style={{ margin: '4px 0 0', fontWeight: 700 }}>
                      {selectedTrackedSuborder.product_name || selectedTrackedSuborder.fuel_type || '-'}
                    </p>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Paper sx={{ p: 1.2, border: '1px solid', borderColor: 'divider' }}>
                    <p style={{ margin: 0, fontSize: 12, color: palette.grey[600] }}>Volume</p>
                    <p style={{ margin: '4px 0 0', fontWeight: 700 }}>
                      {fNumber(selectedTrackedSuborder.volume)}
                    </p>
                  </Paper>
                </Grid>
              </Grid>

              <Paper sx={{ p: 1.2, border: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <div>
                    <p style={{ margin: 0, fontSize: 12, color: palette.grey[600] }}>Destination</p>
                    <p style={{ margin: '4px 0 0', fontWeight: 700 }}>
                      {selectedTrackedSuborder.destination_address || selectedTrackedSuborder.delivery_location || '-'}
                    </p>
                  </div>
                  <Chip
                    size="small"
                    color={this.getSuborderTrackingChip(selectedTrackedSuborder).color}
                    label={this.getSuborderTrackingChip(selectedTrackedSuborder).label}
                  />
                </Stack>
              </Paper>

              <Paper sx={{ p: 1.2, border: '1px solid', borderColor: 'divider' }}>
                <Stepper orientation="vertical" nonLinear>
                  {this.getSuborderTrackingSteps(selectedTrackedSuborder).map((step) => (
                    <Step key={`track-step-${step.key}`} completed={step.completed} active>
                      <StepLabel>{step.label}</StepLabel>
                      <StepContent>
                        <p style={{ margin: '0 0 4px', fontWeight: 700 }}>{step.value}</p>
                        <p style={{ margin: 0, color: palette.grey[600] }}>{step.helper}</p>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleCloseSuborderTrackModal}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showCreateSuborderModal}
        onClose={this.handleCloseCreateSuborderModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Add Suborder {createSuborderOrder?.order_id ? `- Order ${createSuborderOrder.order_id}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {createSuborderLoading && <p>Preparing suborder form...</p>}

          {!createSuborderLoading && (
            <Stack spacing={2}>
              <Grid container spacing={1.2}>
                <Grid size={{ xs: 6 }}>
                  <Paper sx={{ p: 1.1, border: '1px solid', borderColor: 'divider' }}>
                    <p style={{ margin: 0, fontSize: 12, color: palette.grey[600] }}>Total Volume</p>
                    <p style={{ margin: '4px 0 0', fontWeight: 700 }}>{fNumber(createSuborderForm.totalVolume)}</p>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Paper sx={{ p: 1.1, border: '1px solid', borderColor: 'divider' }}>
                    <p style={{ margin: 0, fontSize: 12, color: palette.grey[600] }}>Remaining Volume</p>
                    <p style={{ margin: '4px 0 0', fontWeight: 700 }}>{fNumber(createSuborderForm.remainingVolume)}</p>
                  </Paper>
                </Grid>
              </Grid>

              {toNumber(createSuborderForm.remainingVolume) <= 0 && (
                <p style={{ margin: 0, color: palette.warning.main }}>
                  This order has no remaining volume available for new suborders.
                </p>
              )}

              <TextField
                label="Volume"
                type="number"
                value={createSuborderForm.volume}
                onChange={(event) => this.handleCreateSuborderInput('volume', event.target.value)}
                helperText={
                  toNumber(createSuborderForm.remainingVolume) > 0
                    ? `Maximum allowed: ${createSuborderForm.remainingVolume}`
                    : ''
                }
              />

              <TextField
                select
                label="Destination"
                value={createSuborderForm.destination}
                onChange={(event) => this.handleCreateSuborderInput('destination', event.target.value)}
              >
                {createSuborderAddresses.map((address, index) => {
                  const addressId = address.id || address.address_id || '';
                  const label = address.street_address
                    ? `${address.street_address}, ${address.suburb || ''}`.trim()
                    : `${address.city || ''} ${address.country || ''}`.trim() || `Address ${addressId || index + 1}`;
                  return (
                  <MenuItem key={`address-${addressId || index}`} value={addressId}>
                    {label}
                  </MenuItem>
                  );
                })}
                {createSuborderAddresses.length === 0 && (
                  <MenuItem value="" disabled>
                    No addresses available
                  </MenuItem>
                )}
              </TextField>

              <TextField
                label="Delivery Date"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={createSuborderForm.deliveryDate}
                onChange={(event) => this.handleCreateSuborderInput('deliveryDate', event.target.value)}
              />

              {createSuborderError && (
                <p style={{ margin: 0, color: palette.error.main }}>{createSuborderError}</p>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleCloseCreateSuborderModal} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={this.handleSubmitCreateSuborder}
            disabled={
              createSuborderLoading
              || createSuborderSubmitting
              || toNumber(createSuborderForm.remainingVolume) <= 0
            }
          >
            {createSuborderSubmitting ? 'Creating...' : 'Create Suborder'}
          </Button>
        </DialogActions>
      </Dialog>

      {isAuthorizedRole(["FINANCE-C2"]) && (
        <OrderCards
          orderData={this.state.selectedOrder}
          handleCloseModal={this.handleCloseModal}
          openModal={openModal}
        />
      )}

      {isAuthorizedRole(["FINANCE-L1", "OPERATIONS-C2", "EXECUTIVE-C1", "DEMO"]) && (
        <CustomModal
          openModal={openModal}
          handleCloseModal={this.handleCloseModal}
          modalStyle={this.state.modalStyle}
          modalTitle={isClient? `Order ${this.state.selectedOrder?.order_id || ''}`: this.state.modalTitle}
        >
          <>
            {isClient && (
              <>
                <FormItemlGrid
                  label={"Created by"}
                  value={this.state.selectedOrder?.created_by || '-'}
                  disabled={true}
                />

                <FormItemlGrid
                  label={"Created on"}
                  value={this.state.selectedOrder?.created_date
                    ? moment(this.state.selectedOrder?.created_date).format('YYYY/MM/DD HH:mm:ss')
                    : ''
                  }
                  disabled={true}
                />
              </>
            )}
            {(isClient || isPoPUploaded) && (
              <>
              <FormItemlGrid
                label="PO Number"
              >
                <FormControl fullWidth>
                  <TextField
                    id="po"
                    variant="outlined"
                    value={this.state.orderPo}
                    onChange={(e) => this.setState({
                      orderPo: e.target.value,
                      orderPoError: false,
                    })}
                    error={this.state.orderPoError}
                    helperText={this.state.orderPoError && 'Please provide a PO number'}
                  />
                </FormControl>
              </FormItemlGrid>

              <FormItemlGrid
                label="Reason"
              >
                <FormControl fullWidth>
                  <TextField
                    id="reason"
                    required={true}
                    variant="outlined"
                    value={this.state.reason}
                    onChange={(e) => this.setState({
                      reason: e.target.value,
                      reasonError: false,
                    })}
                    error={this.state.reasonError}
                    helperText={this.state.reasonError && 'Please provide a reason'}
                  />
                </FormControl>
              </FormItemlGrid>
              </>
            )}
            {isClient && (
              <>
              <FormItemlGrid
                label={"Select Depot"}
              >
                <FormControl fullWidth >
                  <TextField
                    displayEmpty
                    select
                    required
                    labelId="depot-select-label"
                    value={this.state.selectedDepot}
                    onChange={(e) => this.handleDepotSelect(e)}
                    error={this.state.selectedDepotError}
                    helperText={this.state.selectedDepotError && 'Please select a depot'}
                  >
                    <MenuItem value="">
                      <em>Select Depot</em>
                    </MenuItem>
                    {depots.map((depot) => (
                      <MenuItem key={depot.depot_id} value={depot.depot_id}>
                        {depot.depot_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>
              </FormItemlGrid>
              </>
            )}
            {(isClient || (!isClient && isPoPUploaded && isAuthorizedRole(["OPERATIONS-C2", "EXECUTIVE-C1", "DEMO"]))) && (
              <FormItemlGrid
                  // label={`${productName} Price per Litre`}
                  label={`Price per Litre`}
                >
                <FormControl fullWidth>
                  <TextField
                    id="price-per-liter"
                    variant="outlined"
                    value={this.state.pricePerLiter}
                    onChange={(e) => this.setState({
                      pricePerLiter: e.target.value,
                      pricePerLiterError: false,
                    })}
                    // disabled={this.state.isDisabledPrice}
                    error={this.state.pricePerLiterError}
                    helperText={this.state.pricePerLiterError && 'Please ensure a price is set'}
                  />
                </FormControl>
              </FormItemlGrid>
            )}

            <Grid container alignItems={'center'} sx={{ mt: 5, }}>
            {(isClient || isPoPUploaded)? (
              <>
              <Grid item size={{ xs: 8 }} sx={{ my: 'auto',  display: 'flex', justifyContent: 'left' }} >
                <FormControl error={this.state.verdictError}>
                  <RadioGroup
                    error
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    onChange={(e) => this.setState({
                      verdict: e.target.value,
                      verdictError: false,
                      reasonError: this.isEmptyField('reason') && e.target.value === 'decline',
                    })}
                  >
                    <RadioButton value="decline" label="Decline" />
                    <RadioButton value="approve" label="Approve" sx={{ml: 2}} />
                  </RadioGroup>
                  <FormHelperText>{this.state.verdictError && 'Please specify an action to perform'}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item size={{ xs: 2 }} sx={{ my: 'auto', display: 'flex', justifyContent: 'right'}}>
                <Button
                  variant='contained'
                  component="label"
                  color='inherit'
                  onClick={() => this.handleCloseModal()}
                >
                  Cancel
                </Button>
              </Grid>
              {/* <Grid item size={{ xs: 4 }}>
                <Button
                  color="error"
                  variant='contained'
                  sx={{ mt: 2 }}
                  onClick={() => { this.submitOrder('decline') }}
                  disabled={!isClient && !isPoPUploaded} // Enable only if isClient is false and PoP is uploaded
                >
                  Decline
                </Button>
              </Grid> */}
              <Grid item size={{ xs: 2 }} sx={{ my: 'auto', display: 'flex', justifyContent: 'right'}}>
                <Button
                  color="secondary"
                  variant='contained'
                  onClick={() => { this.submitOrder() }}
                  disabled={!isClient && !isPoPUploaded} // Enable only if isClient is false and PoP is uploaded
                >
                  Submit
                </Button>
              </Grid>

              </>
            ) :(!isClient && !isPoPUploaded && isAuthorizedRole(["OPERATIONS-C2", "EXECUTIVE-C1", "DEMO"])) && (
                <>
                  {!isFileAttached && (
                    <Grid size={{ xs: 12 }} >
                      <FileDragzone
                        acceptedTypes={{
                          'application/pdf': ['.pdf'],
                        }}
                        fileObjects={this.state.uploadedFiles}
                        handleFileObjects={this.handleProofOfPaymentChange}
                        filesLimit={1}
                        maxFileSizeMB={MAX_FILE_SIZE_MB}
                      />
                    </Grid>
                  )}
                  {isFileAttached && (
                      <Grid item size={{ xs: 12 }} sx={{ my: 'auto', display: 'flex', justifyContent: 'center' }}>
                        <CustomPdfViewer
                          fileUrl={this.state.proofOfPayment ? URL.createObjectURL(this.state.proofOfPayment) : null}
                        />
                      </Grid>
                  )}
                  <Grid item size={{ xs: 10 }} sx={{ mt: 2, display: 'flex', justifyContent: 'left' }} >
                    <FormControl component="fieldset">
                      <FormControlLabel
                        sx={{ margin: 0 }}
                        control={
                          <Switch
                            checked={this.state.skipPfiUpload}
                            onChange={(e) => this.handleChangeToApprovalView(e.target.checked)}
                            name="skipPfiUpload"
                          />
                        }
                        label="Skip PFI Upload?"
                        labelPlacement="start"
                      />
                    </FormControl>
                  </Grid>

                  <Grid item size={{ xs: 2 }} sx={{ mt: 2, display: 'flex', justifyContent: 'right', alignItems: 'right' }}>
                    <Button
                      variant='contained'
                      sx={{ mr: 2 }}
                      component="label"
                      color='inherit'
                      onClick={() => this.handleCloseModal()}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant='contained'
                      component="label"
                      onClick={() => this.handleSubmitDocument()}
                    >
                      Continue
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </>
        </CustomModal>
      )}
      </Paper>
    );
  }
}

AllOrders.propTypes = {
  enableDepotOrderActions: PropTypes.bool,
  filterString: PropTypes.string,
  setUpdateStats: PropTypes.func.isRequired,
  updateAllOders: PropTypes.bool.isRequired,
  setUpdateAllOders: PropTypes.func.isRequired,
};
